import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';

import { HOME_QUEUES } from './homeQueues';

/**
 * Zero in all five queues: the whole point of Home, said plainly.
 *
 * The five cards do not disappear, they COLLAPSE to one row of names and
 * zeroes. A biller who has learned where "Claims to fix" lives should still
 * find it on the day it is empty, and the row is also the proof that the
 * zeroes are real reads rather than a page that failed to load.
 */
export interface HomeEmptyStateProps {
  /** Rows hidden by an archive or a snooze, across all five queues. */
  hiddenCount: number;
}

/**
 * The sun, drawn rather than typed. An emoji would render as whatever font
 * the biller's machine happens to have and cannot take the brand color.
 */
const SunIcon: React.FC = () => (
  <Box
    aria-hidden
    component="svg"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth={2}
    sx={{ color: 'primary.main', height: 64, width: 64 }}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="4.25" />
    <path d="M12 1.75v2.5M12 19.75v2.5M22.25 12h-2.5M4.25 12h-2.5M19.25 4.75l-1.77 1.77M6.52 17.48l-1.77 1.77M19.25 19.25l-1.77-1.77M6.52 6.52 4.75 4.75" />
  </Box>
);

/**
 * The archived-and-snoozed door.
 *
 * `view=needs_correction` and not `view=fix` on purpose: the Claims tab's URL
 * parser only knows today's five keys, and an unknown one silently falls back
 * to All claim work, which would land the biller somewhere they did not ask
 * for. `needs_correction` is the contract's own accepted alias for `fix`, so
 * this link works before AND after packet E teaches the parser the queue
 * keys.
 *
 * TRACKING (GDN-3381, packet E): `hidden` is not yet a param anything parses
 * — `CLAIM_WORK_PARAM_KEYS` in `claims/claimWorkUrlState.ts` owns that list
 * and does not include it, so today the param rides along and does nothing.
 * When GDN-3381 lands the toggle, this link starts arriving with hidden rows
 * shown, and `view=fix` becomes available to replace the alias above.
 */
const HIDDEN_ROWS_URL = '/jotbill?tab=claims&view=needs_correction&hidden=1';

export const HomeEmptyState: React.FC<HomeEmptyStateProps> = ({ hiddenCount }) => (
  <Stack spacing={2}>
    <Stack
      data-testid="home-empty-state"
      spacing={2}
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        px: 3,
        py: 8,
        textAlign: 'center',
      }}
    >
      <SunIcon />
      <Typography sx={{ fontWeight: 700 }} variant="h4">
        Nothing to do right now.
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 540 }} variant="body1">
        All five queues are empty. New work shows up here as claims are built, scrubbed, and
        answered by payers.
      </Typography>
      {/* Only when there is something behind it. `Show archived and snoozed
          · 0` is a door onto an empty room, and it is also what an older
          server produces, which never carries `hidden_count` at all. */}
      {hiddenCount > 0 && (
        <Button
          component={RouterLink}
          size="small"
          sx={{ fontWeight: 600, textTransform: 'none' }}
          to={HIDDEN_ROWS_URL}
          variant="outlined"
        >
          {`Show archived and snoozed · ${hiddenCount}`}
        </Button>
      )}
    </Stack>

    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
      }}
    >
      {HOME_QUEUES.map(queue => (
        <Stack
          key={queue.key}
          data-testid={`home-queue-zero-${queue.key}`}
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            aria-hidden
            sx={{ bgcolor: queue.color, borderRadius: 4, flexShrink: 0, height: 16, width: 3 }}
          />
          <Typography noWrap sx={{ flex: 1, fontWeight: 600 }} variant="body2">
            {queue.label}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            variant="body2"
          >
            0
          </Typography>
        </Stack>
      ))}
    </Box>
  </Stack>
);
